import React, { Fragment, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Column, SelfTableList, FilterState, ListFields, ChooseComponent } from '@np/np_webcomponentlib'
import { API_BUSINESS, API_OPERATIONS_VIEW } from '../../shared/consts/api'
import { ContragentDataType, BindingInfo, ProviderBinding, columnsIdrfcharacteristictype } from './ContragentTypes'
import { ColumnWithCopyToClipboard } from '../../shared/entityOperations/EntityOperationsCustomFC'

import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

function formatToMoscowTime(date: Date | string): string {
  const moscowTimeZone = 'Europe/Moscow';
  const zonedDate = toZonedTime(new Date(date), moscowTimeZone);
  return format(zonedDate, 'yyyy-MM-dd HH:mm:ss');
}

type DatePeriod = '3d' | '7d' | '30d' | 'all';

const periodLabels: Record<DatePeriod, string> = {
  '3d': '3 дня',
  '7d': 'Неделя',
  '30d': 'Месяц',
  'all': 'Всё время',
};

function getDateFromForPeriod(period: DatePeriod): string {
  if (period === 'all') return '';
  const now = new Date();
  const days = period === '3d' ? 3 : period === '7d' ? 7 : 30;
  now.setDate(now.getDate() - days);
  return now.toISOString();
}

const helpTooltipText = `Как работать с привязками:

Фильтры:
\u2022 Мерчант и/или Провайдер \u2014 выберите хотя бы один
\u2022 \u00abНепривязанные\u00bb \u2014 покажет только свободных КА

Выделение:
\u2022 Введите число и нажмите \u00abВыделить\u00bb (все подряд)
  или \u00abВыделить свободных\u00bb (пропуская привязанных)
\u2022 Также можно кликать по чекбоксам вручную

Действия с выделенными:
\u2022 \u00abПривязать\u00bb \u2014 назначит провайдера
\u2022 \u00abОтвязать\u00bb \u2014 снимет привязку
\u2022 \u00abКопировать\u00bb \u2014 скопирует ID в буфер

Привязанные ID отмечены бейджем с именем провайдера.`;

const ContragentList = () => {

  const [activePeriod, setActivePeriod] = useState<DatePeriod>('30d');
  const [forceUpdate, setForceUpdate] = useState(false);
  const [copyCount, setCopyCount] = useState<string>('10');
  const [copyStatus, setCopyStatus] = useState<string>('');
  const [selectedBusiness, setSelectedBusiness] = useState<string>('');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [searchClientID, setSearchClientID] = useState<string>('');
  const [unboundOnly, setUnboundOnly] = useState(false);
  const [showBindChooser, setShowBindChooser] = useState(false);
  const [providerSearch, setProviderSearch] = useState('');
  const providerSearchRef = useRef<HTMLInputElement>(null);

  // Выделение и привязки
  const [selectedIDs, setSelectedIDs] = useState<Set<string>>(new Set());
  const [bindings, setBindings] = useState<Map<string, BindingInfo>>(new Map());
  const [platformProviders, setPlatformProviders] = useState<{id: string, name: string}[]>([]);

  // Refs для доступа из column render без пересоздания columns
  const selectedIDsRef = useRef<Set<string>>(selectedIDs);
  const bindingsRef = useRef<Map<string, BindingInfo>>(bindings);
  selectedIDsRef.current = selectedIDs;
  bindingsRef.current = bindings;

  // Загрузить привязки для текущего мерчанта
  const loadBindings = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const body: any = {};
      if (selectedBusiness) body.idBusiness = selectedBusiness;

      const response = await fetch(`${API_OPERATIONS_VIEW}contragent/bindings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      const list: ProviderBinding[] = data.dataList || data.DataList || [];

      const newMap = new Map<string, BindingInfo>();
      list.forEach((b) => {
        newMap.set(b.clientID, { providerName: b.providerName, idProvider: b.idProvider });
      });
      setBindings(newMap);
    } catch (e) {
      console.error('Load bindings error:', e);
    }
  }, [selectedBusiness]);

  // Загрузить список провайдеров платформы
  const loadPlatformProviders = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BUSINESS}business/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          listFields: [{ fieldName: 'typeBusiness', comparison: 'equalsStrong', listVal: ['provider'] }],
          sort: null,
          searchData: '',
          take: 200,
          skip: 0,
        }),
      });
      const data = await response.json();
      const list = data.dataList || data.DataList || [];
      setPlatformProviders(list.map((p: any) => ({ id: p.idUnique, name: p.name })));
    } catch (e) {
      console.error('Load platform providers error:', e);
    }
  }, []);

  // Загрузить привязки при смене мерчанта
  useEffect(() => {
    if (selectedBusiness || selectedProvider) {
      loadBindings();
    } else {
      setBindings(new Map());
    }
  }, [selectedBusiness, selectedProvider, loadBindings]);

  // Загрузить провайдеров платформы при монтировании
  useEffect(() => {
    loadPlatformProviders();
  }, [loadPlatformProviders]);

  const hasFilter = selectedBusiness || selectedProvider || searchClientID;

  const initialFilter: FilterState = useMemo(() => {
    const filterFields: ListFields[] = [];

    if (selectedBusiness) {
      filterFields.push({
        fieldName: 'idBusiness',
        comparison: 'equalsStrong',
        listVal: [selectedBusiness],
      });
    }

    if (selectedProvider) {
      filterFields.push({
        fieldName: 'providerName',
        comparison: 'equalsStrong',
        listVal: [selectedProvider],
      });
    }

    if (searchClientID) {
      filterFields.push({
        fieldName: 'clientID',
        comparison: 'containsStrong',
        listVal: [searchClientID],
      });
    }

    if (unboundOnly) {
      filterFields.push({
        fieldName: 'unboundOnly',
        comparison: 'equalsStrong',
        listVal: ['true'],
      });
    }

    const dateFromISO = getDateFromForPeriod(activePeriod);
    if (dateFromISO) {
      filterFields.push({
        fieldName: 'dateAdded',
        comparison: 'dateTimeMoreEqual',
        listVal: [dateFromISO],
      });
    }

    const filter: FilterState = {
      listFields: filterFields,
      sort: null,
      searchData: '',
      take: 25,
      skip: 0,
    };

    return filter;
  }, [activePeriod, selectedBusiness, selectedProvider, searchClientID, unboundOnly]);

  const handlePeriodChange = (period: DatePeriod) => {
    setActivePeriod(period);
    setSelectedIDs(new Set());
    setForceUpdate(true);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      return true;
    }
  };

  // Копировать выделенные ID
  const handleCopySelectedIDs = useCallback(async () => {
    if (selectedIDs.size === 0) return;
    const text = Array.from(selectedIDs).join('\n');
    await copyToClipboard(text);
    setCopyStatus(`${selectedIDs.size} ID`);
    setTimeout(() => setCopyStatus(''), 2000);
  }, [selectedIDs]);

  // Получить clientID из текущих видимых строк таблицы (в порядке отображения)
  const getVisibleClientIDs = useCallback((): string[] => {
    const elements = document.querySelectorAll('[data-clientid]');
    const ids: string[] = [];
    elements.forEach(el => {
      const id = el.getAttribute('data-clientid');
      if (id) ids.push(id);
    });
    return ids;
  }, []);

  // Выделить первые N строк (из видимых в таблице)
  const handleSelectN = useCallback(() => {
    const count = parseInt(copyCount) || 10;
    const visibleIDs = getVisibleClientIDs();
    setSelectedIDs(new Set(visibleIDs.slice(0, count)));
  }, [copyCount, getVisibleClientIDs]);

  // Выделить первые N свободных (непривязанных) из видимых строк
  const handleSelectNFree = useCallback(() => {
    const count = parseInt(copyCount) || 10;
    const visibleIDs = getVisibleClientIDs();
    const freeIDs: string[] = [];
    for (const id of visibleIDs) {
      if (!bindingsRef.current.has(id)) {
        freeIDs.push(id);
        if (freeIDs.length >= count) break;
      }
    }
    setSelectedIDs(new Set(freeIDs));
  }, [copyCount, getVisibleClientIDs]);

  // Привязать выделенных к провайдеру
  const handleBind = useCallback(async (providerName: string, idProvider?: string) => {
    if (selectedIDs.size === 0 || !selectedBusiness) return;
    try {
      const token = localStorage.getItem('accessToken');
      const body: any = {
        clientIDs: Array.from(selectedIDs),
        idBusiness: selectedBusiness,
        providerName: providerName,
      };
      if (idProvider) body.idProvider = idProvider;
      await fetch(`${API_OPERATIONS_VIEW}contragent/bind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      setSelectedIDs(new Set());
      setShowBindChooser(false);
      await loadBindings();
      setForceUpdate(true);
    } catch (e) {
      console.error('Bind error:', e);
    }
  }, [selectedIDs, selectedBusiness, loadBindings]);

  // Отвязать выделенных
  const handleUnbind = useCallback(async () => {
    if (selectedIDs.size === 0 || !selectedBusiness) return;
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_OPERATIONS_VIEW}contragent/unbind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          clientIDs: Array.from(selectedIDs),
          idBusiness: selectedBusiness,
        }),
      });
      setSelectedIDs(new Set());
      await loadBindings();
      setForceUpdate(true);
    } catch (e) {
      console.error('Unbind error:', e);
    }
  }, [selectedIDs, selectedBusiness, loadBindings]);

  // Toggle чекбокса
  const toggleSelect = useCallback((clientID: string) => {
    setSelectedIDs(prev => {
      const next = new Set(prev);
      if (next.has(clientID)) {
        next.delete(clientID);
      } else {
        next.add(clientID);
      }
      return next;
    });
  }, []);

  // Для пробрасывания toggleSelect в колонки без пересоздания
  const toggleSelectRef = useRef(toggleSelect);
  toggleSelectRef.current = toggleSelect;

  const [, forceRenderColumns] = useState(0);
  // Обновляем колонки при изменении selectedIDs/bindings
  useEffect(() => {
    forceRenderColumns(c => c + 1);
  }, [selectedIDs, bindings]);

  // Подсветка выделенных строк через DOM
  useEffect(() => {
    const elements = document.querySelectorAll('[data-clientid]');
    elements.forEach(el => {
      const tr = el.closest('tr');
      if (tr) {
        const clientID = el.getAttribute('data-clientid');
        tr.classList.toggle('contragent-row-selected', clientID ? selectedIDs.has(clientID) : false);
        tr.style.cursor = 'pointer';
      }
    });
  }, [selectedIDs]);

  const memoizedColumns = useMemo(() => {
    const columns: Column[] = [
      {
        title: '',
        listFields: null,
        type: 'custom',
        custom: (data: ContragentDataType) => (
          <span data-clientid={data.clientID}>
            <Form.Check
              type="checkbox"
              checked={selectedIDsRef.current.has(data.clientID)}
              onChange={() => toggleSelectRef.current(data.clientID)}
            />
          </span>
        ),
        key: 0,
        withFilter: null,
        class: "text-center",
      },
      {
        title: 'ClientID',
        listFields: {
          fieldName: 'clientID',
          comparison: 'containsStrong',
          listVal: [''],
        },
        type: 'custom',
        custom: (data: ContragentDataType) => (
          <>
            <ColumnWithCopyToClipboard data={data.clientID} title='fe fe-edit eventIcom'></ColumnWithCopyToClipboard>
            {data.clientID}
          </>
        ),
        key: 1,
        withFilter: 'all',
        class: "eventColumn"
      },

      {
        title: 'Провайдер',
        listFields: null,
        type: 'custom',
        custom: (data: ContragentDataType) => {
          const binding = bindingsRef.current.get(data.clientID);
          if (binding) {
            return <span className="badge bg-info">{binding.providerName}</span>;
          }
          return <></>;
        },
        key: 2,
        withFilter: null,
      },

      {
        title: 'Сумма',
        listFields: {
          fieldName: 'totalAmount',
          comparison: 'containsStrong',
          listVal: [''],
        },
        type: 'custom',
        custom: (data: ContragentDataType) => (
          <>{data.totalAmount?.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
        ),
        key: 4,
        withFilter: 'onlySorted',
      },

      {
        title: 'Кол-во операций',
        listFields: {
          fieldName: 'operationCount',
          comparison: 'containsStrong',
          listVal: [''],
        },
        type: 'custom',
        custom: (data: ContragentDataType) => (
          <>{data.operationCount}</>
        ),
        key: 5,
        withFilter: 'onlySorted',
      },

      {
        title: 'Последняя операция',
        listFields: {
          fieldName: 'lastOperationDate',
          comparison: 'containsStrong',
          listVal: [''],
        },
        type: 'custom',
        custom: (data: ContragentDataType) => (
          <>{data.lastOperationDate ? formatToMoscowTime(data.lastOperationDate) : '-'}</>
        ),
        key: 7,
        withFilter: 'onlySorted',
        class: "dateTtimeColumn"
      },
    ];

    return columns;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIDs, bindings]);

  return (
    <Fragment>
      <div className="card p-3">
        <h4 className="mb-3">
          {'КА (Контрагенты)'}
        </h4>

        {/* Строка 1: Фильтры */}
        <div className="d-flex gap-2 mb-2 align-items-center flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold">Мерчант:</span>
            <ChooseComponent
              getDisplayDescription={(selectedData: any) => {
                return selectedData ? selectedData.name : 'Выберите мерчанта'
              }}
              apiUrl={`${API_BUSINESS}business/list`}
              columns={columnsIdrfcharacteristictype}
              selectData={(selectData: any) => {
                setSelectedBusiness(selectData.idUnique);
                setSelectedIDs(new Set());
                setForceUpdate(true);
              }}
              resetData={() => {
                setSelectedBusiness('');
                setSelectedIDs(new Set());
              }}
              initIDUnique={selectedBusiness}
              initialFilterState={{
                listFields: [{ fieldName: "typeBusiness", comparison: "equalsStrong", listVal: ["merchant"] }],
                sort: null,
                searchData: '',
                take: 50,
                skip: 0,
              }}
            >
              Выберите мерчанта
            </ChooseComponent>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold">Провайдер:</span>
            <Form.Select
              size="sm"
              style={{ width: '180px' }}
              value={selectedProvider}
              onChange={(e) => {
                setSelectedProvider(e.target.value);
                if (e.target.value) {
                  setUnboundOnly(false);
                }
                setSelectedIDs(new Set());
                setForceUpdate(true);
              }}
            >
              <option value="">Все</option>
              {platformProviders.map(p => (
                <option key={p.id} value={p.name}>{p.name}</option>
              ))}
            </Form.Select>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold">ClientID:</span>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Поиск по ClientID"
              style={{ width: '220px' }}
              value={searchClientID}
              onChange={(e) => setSearchClientID(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setSelectedIDs(new Set());
                  setForceUpdate(true);
                }
              }}
            />
            {searchClientID && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  setSearchClientID('');
                  setSelectedIDs(new Set());
                  setForceUpdate(true);
                }}
              >
                <i className='fe fe-x'></i>
              </Button>
            )}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => {
              setForceUpdate(true);
              loadBindings();
            }}
            disabled={!hasFilter}
          >
            <i className='fe fe-refresh-cw'></i>
          </Button>

          {(['3d', '7d', '30d', 'all'] as DatePeriod[]).map((period) => (
            <Button
              key={period}
              variant={activePeriod === period ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handlePeriodChange(period)}
              disabled={!hasFilter}
            >
              {periodLabels[period]}
            </Button>
          ))}

          <Button
            variant={unboundOnly ? 'warning' : 'outline-warning'}
            size="sm"
            onClick={() => {
              const newVal = !unboundOnly;
              setUnboundOnly(newVal);
              if (newVal) {
                setSelectedProvider('');
              }
              setSelectedIDs(new Set());
              setForceUpdate(true);
            }}
            disabled={!hasFilter}
          >
            Непривязанные
          </Button>
        </div>

        {/* Строка 2: Работа с выделением */}
        <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
          {/* Группа 1: Выделение */}
          <Form.Control
            type="number"
            size="sm"
            min="1"
            max="1000"
            value={copyCount}
            onChange={(e) => setCopyCount(e.target.value)}
            style={{ width: '70px' }}
            disabled={!hasFilter}
          />
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleSelectN}
            disabled={!hasFilter}
          >
            Выделить
          </Button>
          <Button
            variant="outline-success"
            size="sm"
            onClick={handleSelectNFree}
            disabled={!hasFilter}
          >
            Выделить свободных
          </Button>

          <div className="vr mx-1"></div>

          {/* Группа 2: Действия */}
          <div className="d-flex align-items-center gap-1">
            {showBindChooser ? (
              <div className="d-flex align-items-center gap-1" style={{ position: 'relative' }}>
                <Form.Control
                  ref={providerSearchRef}
                  type="text"
                  placeholder="Поиск провайдера..."
                  style={{ width: '280px', fontSize: '1rem' }}
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                  autoFocus
                />
                {(() => {
                  const query = providerSearch.toLowerCase().trim();
                  const filtered = query
                    ? platformProviders.filter(p => p.name.toLowerCase().includes(query))
                    : platformProviders;
                  return filtered.length > 0 && (
                    <div
                      className="dropdown-menu show"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        width: '280px',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        display: 'block',
                        zIndex: 1050,
                      }}
                    >
                      {filtered.map(p => (
                        <div
                          key={p.id}
                          className="dropdown-item"
                          style={{
                            cursor: 'pointer',
                            fontSize: '1rem',
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleBind(p.name, p.id);
                            setProviderSearch('');
                          }}
                        >
                          {p.name}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <Button variant="outline-secondary" size="sm" onClick={() => { setShowBindChooser(false); setProviderSearch(''); }}>
                  <i className='fe fe-x'></i>
                </Button>
              </div>
            ) : (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowBindChooser(true)}
                disabled={selectedIDs.size === 0 || !selectedBusiness}
              >
                <i className='fe fe-link'></i> Привязать
              </Button>
            )}
          </div>

          <Button
            variant="outline-danger"
            size="sm"
            onClick={handleUnbind}
            disabled={selectedIDs.size === 0 || !selectedBusiness}
          >
            <i className='fe fe-x-circle'></i> Отвязать
          </Button>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleCopySelectedIDs}
            disabled={selectedIDs.size === 0}
          >
            <i className='fe fe-copy'></i> Копировать
          </Button>
          {copyStatus && (
            <span className="text-muted small">{copyStatus}</span>
          )}

          {selectedIDs.size > 0 && (
            <span className="text-muted small ms-1">Выделено: {selectedIDs.size}</span>
          )}

          <div className="vr mx-1"></div>

          {/* Группа 3: Помощь */}
          <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id="help-tooltip" style={{ whiteSpace: 'pre-line', textAlign: 'left', maxWidth: '400px' }}>
                {helpTooltipText}
              </Tooltip>
            }
          >
            <span className="d-inline-flex align-items-center" style={{ cursor: 'help' }}>
              <i className='fe fe-help-circle text-muted' style={{ fontSize: '18px' }}></i>
            </span>
          </OverlayTrigger>
        </div>

        {hasFilter ? (
          <div onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('a, button, th, .fe-clipboard, input[type="checkbox"]')) return;
            const tr = target.closest('tr');
            if (!tr) return;
            const el = tr.querySelector('[data-clientid]');
            if (el) {
              const clientID = el.getAttribute('data-clientid');
              if (clientID) toggleSelectRef.current(clientID);
            }
          }}>
            <style>{`
              tr.contragent-row-selected > td {
                background-color: rgba(0, 0, 0, 0.07) !important;
              }
              @media (prefers-color-scheme: dark) {
                tr.contragent-row-selected > td {
                  background-color: rgba(255, 255, 255, 0.1) !important;
                }
              }
              [data-bs-theme="dark"] tr.contragent-row-selected > td {
                background-color: rgba(255, 255, 255, 0.1) !important;
              }
            `}</style>
            <SelfTableList
              apiUrl={`${API_OPERATIONS_VIEW}contragent/list`}
              columns={memoizedColumns}
              classProp='table table-striped'
              theadClassProp='table'
              forceUpdate={forceUpdate}
              initialFilterState={initialFilter}
              key={`table-${activePeriod}-${selectedBusiness}-${selectedProvider}-${searchClientID}-${unboundOnly}`}
            />
          </div>
        ) : (
          <div className="text-center text-muted py-5">
            Выберите мерчанта, провайдера или введите ClientID
          </div>
        )}
      </div>

    </Fragment>
  )
}

export default ContragentList
